import { notifications } from '@mantine/notifications';
import { NotificationType } from '../types';
import { CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import React from 'react';
import i18n from '../config/i18n';
import {
  NOTIFICATION_ICON_SIZE,
  NOTIFICATION_AUTO_CLOSE_DEFAULT,
  NOTIFICATION_COLOR_SUCCESS,
  NOTIFICATION_COLOR_ERROR,
  NOTIFICATION_COLOR_WARNING,
  NOTIFICATION_COLOR_INFO,
  NOTIFICATION_DEDUPE_ENABLED,
  NOTIFICATION_SHAKE_DURATION_MS,
} from '../constants';

/**
 * Notification service that wraps @mantine/notifications
 * Provides a consistent API with deduplication support to prevent identical notifications
 *
 * Features:
 * - ID-based deduplication (same ID replaces existing notification)
 * - Shake animation for re-triggered notifications
 * - Centralized constants (no magic numbers)
 * - i18n support for titles
 * - Consistent icon sizing and colors
 */
export class NotificationService {
  /**
   * Track active notification IDs and messages for shake animation
   * When a notification is re-triggered (same ID), apply shake animation
   * Map<id, message> - stores both ID and message for update() calls
   */
  private static activeNotifications = new Map<string, string>();
  /**
   * Generate unique notification ID based on type and message
   * Same type + message = same ID = replacement instead of duplicate
   *
   * @param type - Notification type (success, error, warning, info)
   * @param message - Notification message content
   * @returns Unique ID string for deduplication
   */
  private static generateNotificationId(type: NotificationType, message: string): string {
    // Simple hash: concatenate type and message
    // Mantine will replace existing notification with same ID
    return `${type}-${message}`;
  }

  /**
   * Apply shake animation to a notification when re-triggered
   * Temporarily adds 'retrigger' CSS class for shake animation
   *
   * @param id - Notification ID to shake
   */
  private static applyShake(id: string): void {
    const message = this.activeNotifications.get(id);
    if (!message) return; // Safety check - notification might have been closed

    // Add shake animation class
    notifications.update({
      id,
      message,
      classNames: { root: 'retrigger' },
    });

    // Remove animation class after animation completes
    setTimeout(() => {
      notifications.update({
        id,
        message,
        classNames: { root: '' },
      });
    }, NOTIFICATION_SHAKE_DURATION_MS);
  }

  /**
   * Core notification display logic (DRY - used by all show methods)
   * Handles ID generation, deduplication, re-trigger detection, and shake animation
   *
   * @param type - Notification type (success, error, warning, info)
   * @param message - Notification message content
   * @param autoClose - Auto-close after timeout
   * @param config - Notification configuration (title, color, icon)
   */
  private static showNotification(
    type: NotificationType,
    message: string,
    autoClose: boolean,
    config: {
      title: string;
      color: string;
      icon: React.ReactElement;
    }
  ): void {
    const id = NOTIFICATION_DEDUPE_ENABLED
      ? this.generateNotificationId(type, message)
      : undefined;

    // Check if notification already exists (re-trigger)
    const isRetrigger = id && this.activeNotifications.has(id);

    notifications.show({
      id,
      title: config.title,
      message,
      color: config.color,
      icon: config.icon,
      autoClose: autoClose ? NOTIFICATION_AUTO_CLOSE_DEFAULT : false,
      withCloseButton: true,
      onClose: () => {
        // Remove from tracking when closed
        if (id) this.activeNotifications.delete(id);
      },
    });

    // Track active notification with its message
    if (id) this.activeNotifications.set(id, message);

    // Apply shake animation if re-triggered
    if (isRetrigger && id) {
      this.applyShake(id);
    }
  }

  /**
   * Show a success notification
   * @param message - Notification message
   * @param autoClose - Auto-close after timeout (default: false)
   */
  static showSuccess(message: string, autoClose: boolean = false) {
    this.showNotification(NotificationType.Success, message, autoClose, {
      title: i18n.t('notifications.title.success'),
      color: NOTIFICATION_COLOR_SUCCESS,
      icon: React.createElement(CheckCircle, { size: NOTIFICATION_ICON_SIZE }),
    });
  }

  /**
   * Show an error notification
   * @param message - Notification message
   * @param autoClose - Auto-close after timeout (default: false)
   */
  static showError(message: string, autoClose: boolean = false) {
    this.showNotification(NotificationType.Error, message, autoClose, {
      title: i18n.t('notifications.title.error'),
      color: NOTIFICATION_COLOR_ERROR,
      icon: React.createElement(AlertCircle, { size: NOTIFICATION_ICON_SIZE }),
    });
  }

  /**
   * Show a warning notification
   * @param message - Notification message
   * @param autoClose - Auto-close after timeout (default: false)
   */
  static showWarning(message: string, autoClose: boolean = false) {
    this.showNotification(NotificationType.Warning, message, autoClose, {
      title: i18n.t('notifications.title.warning'),
      color: NOTIFICATION_COLOR_WARNING,
      icon: React.createElement(AlertTriangle, { size: NOTIFICATION_ICON_SIZE }),
    });
  }

  /**
   * Show an info notification
   * @param message - Notification message
   * @param autoClose - Auto-close after timeout (default: false)
   */
  static showInfo(message: string, autoClose: boolean = false) {
    this.showNotification(NotificationType.Info, message, autoClose, {
      title: i18n.t('notifications.title.info'),
      color: NOTIFICATION_COLOR_INFO,
      icon: React.createElement(Info, { size: NOTIFICATION_ICON_SIZE }),
    });
  }

  /**
   * Show a notification by type
   */
  static show(type: NotificationType, message: string, autoClose: boolean = false) {
    switch (type) {
      case NotificationType.Success:
        this.showSuccess(message, autoClose);
        break;
      case NotificationType.Error:
        this.showError(message, autoClose);
        break;
      case NotificationType.Warning:
        this.showWarning(message, autoClose);
        break;
      case NotificationType.Info:
        this.showInfo(message, autoClose);
        break;
      default:
        this.showInfo(message, autoClose);
    }
  }

  /**
   * Hide all notifications
   */
  static hideAll() {
    notifications.clean();
  }

  /**
   * Clear notification queue
   */
  static clearQueue() {
    notifications.cleanQueue();
  }
}